(() => {
  if (window.__puffleEditorialLoaded) return;
  window.__puffleEditorialLoaded = true;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class PuffleSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-slide-to]')];
      this.statuses = [...this.querySelectorAll('[data-slide-status]')];
      this.toggleButtons = [...this.querySelectorAll('[data-slide-toggle]')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'));
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion.matches;
      this.rotationReady = false;
      this.interval = Number(this.dataset.interval) || 5000;
      this.querySelector('[data-slide-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
      this.querySelector('[data-slide-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.slideTo), true)));
      this.toggleButtons.forEach((button) => button.addEventListener('click', () => {
          this.rotationReady = true;
          this.removeAutoplayGate();
          this.autoplay = !this.autoplay;
          this.autoplay ? this.start() : this.stop();
          this.syncToggle();
        }));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.onVisibilityChange = () => document.hidden ? this.stop() : this.start();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.onFirstInteraction = (event) => {
        if (event?.target instanceof Element && event.target.closest('[data-slide-toggle]')) return;
        this.rotationReady = true;
        this.removeAutoplayGate();
        this.start();
      };
      if (this.autoplay && this.slides.length > 1) this.addAutoplayGate();
      this.show(this.index, false);
      this.syncToggle();
      this.start();
    }
    show(index, announce) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
        slide.querySelectorAll('video').forEach((video) => {
          if (active && this.autoplay && !reduceMotion.matches) video.play().catch(() => {});
          else video.pause();
        });
      });
      this.dots.forEach((dot) => dot.setAttribute('aria-current', String(Number(dot.dataset.slideTo) === this.index)));
      if (this.statuses.length) {
        const current = String(this.index + 1).padStart(2, '0');
        const total = String(this.slides.length).padStart(2, '0');
        this.statuses.forEach((status) => {
          status.textContent = `${current} / ${total}`;
          if (announce) status.setAttribute('data-announced', 'true');
        });
      }
      this.restart();
    }
    start() {
      if (!this.autoplay) return;
      this.slides[this.index]?.querySelectorAll('video').forEach((video) => {
        if (!reduceMotion.matches) video.play().catch(() => {});
      });
      if (this.rotationReady && !this.timer && this.slides.length > 1) this.timer = window.setInterval(() => this.show(this.index + 1, false), this.interval);
    }
    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
      this.slides?.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
    }
    restart() { if (this.timer) { this.stop(); this.start(); } }
    addAutoplayGate() {
      window.addEventListener('pointerdown', this.onFirstInteraction, { passive: true });
      window.addEventListener('touchstart', this.onFirstInteraction, { passive: true });
      window.addEventListener('keydown', this.onFirstInteraction);
      window.addEventListener('scroll', this.onFirstInteraction, { passive: true });
    }
    removeAutoplayGate() {
      window.removeEventListener('pointerdown', this.onFirstInteraction);
      window.removeEventListener('touchstart', this.onFirstInteraction);
      window.removeEventListener('keydown', this.onFirstInteraction);
      window.removeEventListener('scroll', this.onFirstInteraction);
    }
    syncToggle() {
      if (!this.toggleButtons.length) return;
      const playLabel = this.dataset.playLabel || 'Play';
      const pauseLabel = this.dataset.pauseLabel || 'Pause';
      this.toggleButtons.forEach((button) => {
        button.setAttribute('aria-label', this.autoplay ? pauseLabel : playLabel);
        button.querySelector('[data-play-icon]')?.toggleAttribute('hidden', this.autoplay);
        button.querySelector('[data-pause-icon]')?.toggleAttribute('hidden', !this.autoplay);
      });
    }
    disconnectedCallback() {
      this.stop();
      this.removeAutoplayGate();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  class PuffleHotspots extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-hotspot-button]').forEach((button) => {
        button.addEventListener('click', () => {
          const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
          const open = button.getAttribute('aria-expanded') === 'true';
          this.querySelectorAll('[data-hotspot-button]').forEach((item) => item.setAttribute('aria-expanded', 'false'));
          this.querySelectorAll('[data-hotspot-panel]').forEach((item) => { item.hidden = true; });
          button.setAttribute('aria-expanded', String(!open));
          if (panel) panel.hidden = open;
        });
      });
    }
  }

  class PuffleMediaToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-media-toggle]');
      this.media = this.querySelector('video');
      if (!this.button || !this.media) return;
      this.button.addEventListener('click', () => this.toggle());
      this.media.addEventListener('play', () => this.sync());
      this.media.addEventListener('pause', () => this.sync());
      this.sync();
    }
    toggle() {
      if (this.media.paused) {
        if (this.dataset.playWithSound === 'true') this.media.muted = false;
        this.media.play().catch(() => {});
      } else {
        this.media.pause();
      }
    }
    sync() {
      const paused = this.media.paused;
      const playLabel = this.dataset.playLabel || 'Play video';
      const pauseLabel = this.dataset.pauseLabel || 'Pause video';
      this.button.setAttribute('aria-label', paused ? playLabel : pauseLabel);
      this.button.querySelector('[data-play-icon]')?.toggleAttribute('hidden', !paused);
      this.button.querySelector('[data-pause-icon]')?.toggleAttribute('hidden', paused);
    }
  }

  class PuffleCounter extends HTMLElement {
    connectedCallback() {
      const value = Number(this.dataset.value);
      if (!Number.isFinite(value)) return;
      if (reduceMotion.matches || !('IntersectionObserver' in window)) { this.textContent = this.format(value); return; }
      this.textContent = this.format(0);
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / 900, 1);
          this.textContent = this.format(Math.round(value * (1 - Math.pow(1 - progress, 3))));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: .35 });
      observer.observe(this);
    }
    format(value) { return `${this.dataset.prefix || ''}${new Intl.NumberFormat(document.documentElement.lang).format(value)}${this.dataset.suffix || ''}`; }
  }

  class PuffleAutoTrack extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-auto-track]');
      this.button = this.querySelector('[data-autotrack-toggle]');
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion.matches;
      this.interval = Number(this.dataset.interval) || 4000;
      this.button?.addEventListener('click', () => {
        this.autoplay = !this.autoplay;
        this.autoplay ? this.start() : this.stop();
        this.sync();
      });
      this.addEventListener('mouseenter', () => this.stop(false));
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop(false));
      this.addEventListener('focusout', () => this.start());
      this.sync();
      this.start();
    }
    advance() {
      const item = this.track?.firstElementChild;
      if (!this.track || !item) return;
      const end = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 8;
      this.track.scrollTo({ left: end ? 0 : this.track.scrollLeft + item.getBoundingClientRect().width + 20, behavior: 'smooth' });
    }
    start() { if (this.autoplay && !this.timer) this.timer = window.setInterval(() => this.advance(), this.interval); }
    stop(disable = false) { window.clearInterval(this.timer); this.timer = null; if (disable) this.autoplay = false; }
    sync() { if (this.button) this.button.textContent = this.autoplay ? 'Pause' : 'Play'; }
    disconnectedCallback() { this.stop(); }
  }

  class PuffleReels extends HTMLElement {
    connectedCallback() {
      this.videos = [...this.querySelectorAll('video')];
      if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const autoplay = entry.target.closest('[data-reel-autoplay]')?.dataset.reelAutoplay === 'true';
          if (entry.isIntersecting && entry.intersectionRatio >= .7 && autoplay) entry.target.play().catch(() => {});
          else entry.target.pause();
        });
      }, { threshold: [.25, .7] });
      this.videos.forEach((video) => this.observer.observe(video));
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }

  class PuffleBundleFeature extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select[name="id"]');
      this.price = this.querySelector('[data-bundle-price]');
      this.compare = this.querySelector('[data-bundle-compare]');
      this.select?.addEventListener('change', () => this.sync());
    }
    sync() {
      const option = this.select?.selectedOptions[0];
      if (!option) return;
      if (this.price) this.price.textContent = option.dataset.price || '';
      if (this.compare) {
        this.compare.textContent = option.dataset.compare || '';
        this.compare.hidden = !option.dataset.compare;
      }
    }
  }

  class PuffleMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.button = this.querySelector('[data-marquee-toggle]');
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion.matches;
      this.button?.addEventListener('click', () => {
        this.autoplay = !this.autoplay;
        this.sync();
      });
      this.sync();
    }
    sync() {
      this.classList.toggle('is-paused', !this.autoplay);
      if (!this.button) return;
      const playLabel = this.dataset.playLabel || 'Play';
      const pauseLabel = this.dataset.pauseLabel || 'Pause';
      this.button.setAttribute('aria-label', this.autoplay ? pauseLabel : playLabel);
      this.button.setAttribute('aria-pressed', String(!this.autoplay));
      this.button.querySelector('[data-play-icon]')?.toggleAttribute('hidden', this.autoplay);
      this.button.querySelector('[data-pause-icon]')?.toggleAttribute('hidden', !this.autoplay);
    }
  }

  if (!customElements.get('puffle-slideshow')) customElements.define('puffle-slideshow', PuffleSlideshow);
  if (!customElements.get('puffle-hotspots')) customElements.define('puffle-hotspots', PuffleHotspots);
  if (!customElements.get('puffle-media-toggle')) customElements.define('puffle-media-toggle', PuffleMediaToggle);
  if (!customElements.get('puffle-counter')) customElements.define('puffle-counter', PuffleCounter);
  if (!customElements.get('puffle-auto-track')) customElements.define('puffle-auto-track', PuffleAutoTrack);
  if (!customElements.get('puffle-reels')) customElements.define('puffle-reels', PuffleReels);
  if (!customElements.get('puffle-bundle-feature')) customElements.define('puffle-bundle-feature', PuffleBundleFeature);
  if (!customElements.get('puffle-marquee')) customElements.define('puffle-marquee', PuffleMarquee);

  if (reduceMotion.matches) document.querySelectorAll('video[autoplay]').forEach((video) => video.pause());

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-scroll-track]');
    if (!button) return;
    const track = document.getElementById(button.dataset.scrollTrack);
    if (track) track.scrollBy({ left: track.clientWidth * Number(button.dataset.direction || 1), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });
})();

(() => {
  if (customElements.get('puffle-review-slider')) return;

  class PuffleReviewSlider extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-static-grid')) return;

      this.track = this.querySelector('[data-review-track]');
      this.navigation = this.querySelector('[data-review-navigation]');
      this.range = this.querySelector('[data-review-range]');
      this.total = this.querySelector('[data-review-total]');
      this.progress = this.querySelector('[data-review-progress]');
      this.status = this.querySelector('[data-review-status]');
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      this.slides = [...this.querySelectorAll('[data-review-slide]')];
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.track || !this.navigation || !this.slides.length) return;

      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `Review ${index + 1} of ${this.slides.length}`);
      });

      this.onPrevious = () => this.advance(-1);
      this.onNext = () => this.advance(1);
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onScroll = () => {
        if (!this.frame) {
          this.frame = window.requestAnimationFrame(() => {
            this.frame = null;
            this.sync(false);
          });
        }
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.sync(true), 140);
      };
      this.onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.sync(false), 80);
      };

      this.previous?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('keydown', this.onKeydown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      window.requestAnimationFrame(() => this.sync(false));
    }

    visibleCount() {
      const firstSlide = this.slides[0];
      if (!firstSlide) return 0;
      const slideWidth = firstSlide.getBoundingClientRect().width;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      if (!slideWidth) return 1;
      return Math.max(1, Math.min(this.slides.length, Math.floor(((this.track.clientWidth + gap) / (slideWidth + gap)) + 0.05)));
    }

    currentIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const rtl = window.getComputedStyle(this.track).direction === 'rtl';
      const trackEdge = rtl ? trackRect.right : trackRect.left;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideEdge = rtl ? rect.right : rect.left;
        const distance = Math.abs(slideEdge - trackEdge);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    advance(direction) {
      const visible = this.visibleCount();
      const maximumStart = Math.max(0, this.slides.length - visible);
      const target = direction > 0
        ? Math.min(this.currentIndex() + visible, maximumStart)
        : Math.max(this.currentIndex() - visible, 0);
      this.goTo(target);
    }

    goTo(index) {
      const target = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      if (!target) return;
      target.scrollIntoView({
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.sync(true), this.reduceMotion.matches ? 0 : 320);
    }

    handleKeydown(event) {
      const rtl = window.getComputedStyle(this.track).direction === 'rtl';
      let handled = true;

      switch (event.key) {
        case 'ArrowRight':
          this.advance(rtl ? -1 : 1);
          break;
        case 'ArrowLeft':
          this.advance(rtl ? 1 : -1);
          break;
        case 'Home':
          this.goTo(0);
          break;
        case 'End':
          this.goTo(Math.max(0, this.slides.length - this.visibleCount()));
          break;
        default:
          handled = false;
      }

      if (handled) event.preventDefault();
    }

    sync(announce) {
      const visible = this.visibleCount();
      const current = this.currentIndex();
      const end = Math.min(current + visible, this.slides.length);
      const hasOverflow = this.slides.length > visible && this.track.scrollWidth > this.track.clientWidth + 2;
      const pad = (value) => String(value).padStart(2, '0');

      this.navigation.hidden = !hasOverflow;
      if (!hasOverflow) return;

      if (this.range) this.range.textContent = visible > 1 ? `${pad(current + 1)}–${pad(end)}` : pad(current + 1);
      if (this.total) this.total.textContent = pad(this.slides.length);
      if (this.progress) this.progress.style.setProperty('--review-progress', String(end / this.slides.length));
      if (this.previous) this.previous.disabled = current <= 0;
      if (this.next) this.next.disabled = end >= this.slides.length;

      if (announce && this.status) {
        const message = visible > 1
          ? `Showing reviews ${current + 1} to ${end} of ${this.slides.length}`
          : `Showing review ${current + 1} of ${this.slides.length}`;
        if (message !== this.lastAnnouncement) {
          this.status.textContent = message;
          this.lastAnnouncement = message;
        }
      }
    }

    disconnectedCallback() {
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('keydown', this.onKeydown);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.frame);
      window.clearTimeout(this.scrollTimer);
      window.clearTimeout(this.resizeTimer);
    }
  }

  customElements.define('puffle-review-slider', PuffleReviewSlider);
})();
